import React, { useState } from 'react';

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@600,700&display=swap');

:root {
  --navy: #06112b; --blue: #1c73ba; --gold: #f5a623;
  --muted: #94a3b8; --border: rgba(28, 115, 186, 0.15);
}

.la-container {
  background: var(--navy); color: #fff; font-family: 'DM Sans', sans-serif;
  min-height: 100vh; padding: 80px 5%;
}

.la-header { text-align: center; max-width: 800px; margin: 0 auto 60px; }
.la-header h1 { font-family: 'Clash Display', sans-serif; font-size: 3.5rem; margin-bottom: 10px; }
.la-header p { color: var(--muted); font-size: 1.1rem; }

.stepper-container { max-width: 900px; margin: 0 auto; position: relative; }

.step-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: 24px; padding: 40px;
  margin-bottom: 30px; display: flex; gap: 30px;
  transition: 0.3s;
}

@media (max-width: 768px) {
  .step-card { flex-direction: column; padding: 25px; }
}

.step-badge {
  background: var(--blue); width: 50px; height: 50px; border-radius: 15px;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 1.2rem; flex-shrink: 0;
}

.step-content { flex: 1; }
.step-content h2 { font-family: 'Clash Display', sans-serif; margin-bottom: 10px; color: var(--gold); }
.step-content p { color: var(--muted); line-height: 1.6; margin-bottom: 20px; }

.media-wrapper {
  width: 100%; border-radius: 16px; overflow: hidden;
  border: 1px solid var(--border); background: #000;
  margin-top: 20px; position: relative;
}

.media-wrapper video, .media-wrapper img {
  width: 100%; height: auto; display: block;
}

.media-caption {
  padding: 10px; font-size: 11px; color: var(--muted);
  background: rgba(0,0,0,0.6); text-align: center;
  text-transform: uppercase; letter-spacing: 1px;
}

.os-toggle {
  display: flex; background: rgba(255,255,255,0.05); 
  padding: 5px; border-radius: 12px; width: fit-content; margin-bottom: 30px;
}

.os-btn {
  padding: 10px 25px; border-radius: 8px; border: none; cursor: pointer;
  background: transparent; color: var(--muted); font-weight: 600; transition: 0.3s;
}

.os-btn.active { background: var(--blue); color: #fff; }

.btn-link { color: var(--gold); text-decoration: none; font-weight: 700; border-bottom: 1px solid; }
`;

export default function LinkAccessories() {
  const [os, setOs] = useState('android');

  return (
    <div className="la-container">
      <style>{STYLES}</style>

      <header className="la-header">
        <h1>Setup <span>Guide</span></h1>
        <p>Follow these 3 simple steps to bring your Flap accessory to life.</p>
      </header>

      <div className="stepper-container">
        
        {/* STEP 1: REGISTRATION VIDEO */}
        <div className="step-card">
          <div className="step-badge">01</div>
          <div className="step-content">
            <h2>Digital Profile</h2>
            <p>First, create your digital presence at <a href="https://flapcard.com.np" className="btn-link" target="_blank" rel="noreferrer">flapcard.com.np</a>. Add your social links, WhatsApp, and contact details.</p>
            <div className="media-wrapper">
              <video autoPlay loop muted playsInline>
                <source src="images/setup-register.mp4" type="video/mp4" />
              </video>
              <div className="media-caption">Process: Mobile Registration</div>
            </div>
          </div>
        </div>

        {/* STEP 2: NAVIGATION VIDEO */}
        <div className="step-card">
          <div className="step-badge">02</div>
          <div className="step-content">
            <h2>Get Your Link</h2>
            <p>Log in to your dashboard and navigate to the <b>Menu &gt; Write NFC</b>. This is where you'll find the link that "powers" your accessory.</p>
            <div className="media-wrapper">
              <video autoPlay loop muted playsInline>
                <source src="images/nav-guide.mp4" type="video/mp4" />
              </video>
              <div className="media-caption">Navigation: Locating 'Write NFC'</div>
            </div>
          </div>
        </div>

        {/* STEP 3: PLATFORM SPECIFIC VIDEO */}
        <div className="step-card">
          <div className="step-badge">03</div>
          <div className="step-content">
            <h2>Write to Accessory</h2>
            <p>Choose your device to see exact placement and writing instructions:</p>
            
            <div className="os-toggle">
              <button 
                className={`os-btn ${os === 'android' ? 'active' : ''}`} 
                onClick={() => setOs('android')}
              >
                Android
              </button>
              <button 
                className={`os-btn ${os === 'ios' ? 'active' : ''}`} 
                onClick={() => setOs('ios')}
              >
                iOS / iPhone
              </button>
            </div>

            {os === 'android' ? (
              <div className="os-content">
                <p>• Ensure <b>NFC is ON</b> in Settings.<br/>
                   • Click <b>"Write via Browser"</b> on your dashboard.<br/>
                   • Tap the accessory to the <b>middle-back</b> of your phone.</p>
                <div className="media-wrapper">
                  {/* The 'key' prop forces video reload when state changes */}
                  <video key="android" autoPlay loop muted playsInline>
                    <source src="images/android-tap.mp4" type="video/mp4" />
                  </video>
                  <div className="media-caption">Android: NFC Sweet Spot</div>
                </div>
              </div>
            ) : (
              <div className="os-content">
                <p>• Copy your Profile Link from Flap Dashboard.<br/>
                   • Install <b>NFC Tools</b> from App Store.<br/>
                   • Go to <b>Write &gt; Add Record &gt; URL</b> &gt; Paste Link.<br/>
                   • Tap <b>"Write"</b> and touch accessory to the <b>top edge</b> of iPhone.</p>
                <div className="media-wrapper">
                  {/* The 'key' prop forces video reload when state changes */}
                  <video key="ios" autoPlay loop muted playsInline>
                    <source src="images/ios-app-guide.mp4" type="video/mp4" />
                  </video>
                  <div className="media-caption">iOS: NFC Tools Setup</div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      <footer style={{ textAlign: 'center', marginTop: '60px', color: 'var(--muted)', fontSize: '0.9rem' }}>
        Need help? Contact our support team via WhatsApp.
      </footer>
    </div>
  );
}